/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect } from "react";
import { Gender, DayOfWeek } from "../../../util/types";
import Modal from "../../../components/Modal";
// import * as api from "../../services/api";
import apiService from "services/apiService";
import { colors } from "themes";
import Loader from "components/Loader";
import { useForm } from "react-hook-form";
// import { FormProvider, useForm } from "react-hook-form";
// import PhoneNumberInput from "components/PhoneNumberInput";
import {
  DEFAULT_COUNTRY_CODE,
  joinPhoneNumber,
  splitPhoneNumber,
  // validatePhoneForCountry,
} from "util/phoneCountry";

interface EmployeeRosterPayload {
  userId?: number;
  email: string;
  displayName: string;
  phoneNumber: string;
  gender: Gender;
  efficiency: number;
  availability: DayOfWeek[];
  timePreference: [number, number];
}

interface EmployeeData {
  id: number;
  displayName: string;
  email: string;
  mobileNumber: string;
  gender: Gender;
  efficiency?: number;
  availability?: DayOfWeek[];
  timePreference?: [number, number];
}

interface AgentFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (
    agent: EmployeeRosterPayload,
    id?: number,
    isEdit?: boolean,
  ) => Promise<void>;
  agent: EmployeeData | null;
}

interface ValidationErrors {
  displayName?: string;
  email?: string;
  phoneNumber?: string;
  availability?: string;
  timePreference?: string;
}

const initialFormData: EmployeeRosterPayload = {
  displayName: "",
  email: "",
  phoneNumber: "",
  gender: "Other",
  efficiency: 80,
  availability: [],
  timePreference: [9.0, 17.0],
};

const AgentFormModal: React.FC<AgentFormModalProps> = ({
  isOpen,
  onClose,
  onSave,
  agent,
}) => {
  const [formData, setFormData] = useState<EmployeeRosterPayload>(initialFormData);
  const [initialData, setInitialData] = useState<EmployeeRosterPayload | null>(null);
  const [agentsList, setAgentsList] = useState<any[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});
  const [isExistingRosterAgent, setIsExistingRosterAgent] = useState(false);
  const [isCheckingExistence, setIsCheckingExistence] = useState(false);
  const phoneForm = useForm({
    defaultValues: { phoneNumber: "", phoneCountryCode: DEFAULT_COUNTRY_CODE },
  });
  const {
    reset: resetPhoneForm,
    watch: watchPhoneForm,
    // getValues: getPhoneValues,
    // setError: setPhoneError,
    clearErrors: clearPhoneErrors,
  } = phoneForm;

  useEffect(() => {
    const { countryCode, nationalNumber } = splitPhoneNumber(initialData?.phoneNumber);
    resetPhoneForm({ phoneNumber: nationalNumber, phoneCountryCode: countryCode });
  }, [initialData, resetPhoneForm]);

  useEffect(() => {
    const subscription = watchPhoneForm((values, { type }) => {
      if (type !== "change") return;
      setFormData((prev) => ({
        ...prev,
        phoneNumber: joinPhoneNumber(values.phoneCountryCode, values.phoneNumber),
      }));
    });
    return () => subscription.unsubscribe();
  }, [watchPhoneForm]);

  // Helper function to convert full day names to abbreviated format
  const normalizeAvailability = (availability: string[]): DayOfWeek[] => {
    const dayMapping: Record<string, DayOfWeek> = {
      Monday: "Mon",
      Tuesday: "Tue",
      Wednesday: "Wed",
      Thursday: "Thu",
      Friday: "Fri",
      Saturday: "Sat",
      Sunday: "Sun",
    };

    return availability.map((day) => {
      // If it's already abbreviated, return as is
      if (["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].includes(day)) {
        return day as DayOfWeek;
      }
      // If it's a full name, convert to abbreviated
      return dayMapping[day] || (day as DayOfWeek);
    });
  };

  const getFormDataFromRosterAgent = (
    existingAgent: any,
  ): EmployeeRosterPayload => ({
    userId: existingAgent.userId,
    displayName: existingAgent.displayName || "",
    email: existingAgent.email || "",
    phoneNumber: existingAgent.phoneNumber || "",
    gender: existingAgent.gender || "Other",
    efficiency: existingAgent.efficiency || 80,
    availability: normalizeAvailability(existingAgent.availability || []),
    timePreference: existingAgent.timePreference || [9.0, 17.0],
  });

  const getFormDataFromEmployee = (
    employee: EmployeeData,
  ): EmployeeRosterPayload => ({
    userId: employee.id,
    displayName: employee.displayName || "",
    email: employee.email || "",
    phoneNumber: employee.mobileNumber || "",
    gender: employee.gender || "Other",
    efficiency: employee.efficiency || 80,
    availability: normalizeAvailability(employee.availability || []),
    timePreference: employee.timePreference || [9.0, 17.0],
  });

  const resetModalState = () => {
    setIsExistingRosterAgent(false);
    setAgentsList([]);
  };

  const initializeForm = async () => {
    if (!agent || !isOpen) {
      resetModalState();
      return;
    }

    setIsCheckingExistence(true);

    try {
      const response = await apiService.get("/api/roster-data/getAll");
      const data = response.data;

      setAgentsList(data);

      const existingAgent = data.find(
        (a: any) => a.userId === agent.id,
      );

      if (existingAgent) {
        setIsExistingRosterAgent(true);
        const newFormData = getFormDataFromRosterAgent(existingAgent);
        setFormData(newFormData);
        // Deep copy baseline to prevent reference sharing
        setInitialData(JSON.parse(JSON.stringify(newFormData)));
        return;
      }

      setIsExistingRosterAgent(false);
      const newFormData = getFormDataFromEmployee(agent);
      setFormData(newFormData);
      // Deep copy baseline to prevent reference sharing
      setInitialData(JSON.parse(JSON.stringify(newFormData)));
    } catch (err) {
      console.error("Error checking agent existence:", err);

      resetModalState();

      if (agent) {
        const newFormData = getFormDataFromEmployee(agent);
        setFormData(newFormData);
        setInitialData(JSON.parse(JSON.stringify(newFormData)));
      }
    } finally {
      setIsCheckingExistence(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      initializeForm();
    } else {
      setFormData(initialFormData);
      setInitialData(null);
    }

    setError(null);
    setValidationErrors({});
  }, [agent, isOpen]);

  // Validation functions (only for the editable fields — availability & time)
  const validateAvailability = (
    availability: DayOfWeek[],
  ): string | undefined => {
    if (availability.length === 0) {
      return "At least one day must be selected";
    }
    return undefined;
  };

  const validateTimePreference = (
    timePreference: [number, number],
  ): string | undefined => {
    if (timePreference[0] >= timePreference[1]) {
      return "Start time must be less than end time";
    }
    return undefined;
  };

  const checkDuplicateEmail = async (
    email: string,
    currentAgentId?: number,
  ): Promise<string | undefined> => {
    try {
      const data = agentsList.length
        ? agentsList
        : (await apiService.get("/api/roster-data/getAll")).data;
      const duplicate = data.find(
        (a: any) =>
          a.email.toLowerCase() === email.toLowerCase() &&
          a.userId !== currentAgentId,
      );
      if (duplicate) {
        return `Email already exists for agent: ${duplicate.displayName}`;
      }
      return undefined;
    } catch (err) {
      console.error("Error checking duplicate email:", err);
      return undefined; // Don't block form submission if check fails
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === "efficiency" ? parseFloat(value) : value,
    }));
  };

  const handleTimePreferenceChange = (index: 0 | 1, value: string) => {
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return;

    setFormData((prev) => {
      const newTimePreference: [number, number] = [...prev.timePreference] as [
        number,
        number,
      ];
      newTimePreference[index] = numValue;
      return { ...prev, timePreference: newTimePreference };
    });
  };

  const handleAvailabilityChange = (day: DayOfWeek) => {
    setFormData((prev) => {
      const newAvailability = prev.availability.includes(day)
        ? prev.availability.filter((d) => d !== day)
        : [...prev.availability, day];
      return { ...prev, availability: newAvailability };
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    setValidationErrors({});
    clearPhoneErrors();

    // Name / Email / Phone / Gender are read-only (sourced from the employee
    // record), so only the editable fields below are validated.
    const errors: ValidationErrors = {};

    // Availability validation
    const availabilityError = validateAvailability(formData.availability);
    if (availabilityError) errors.availability = availabilityError;

    // Time preference validation
    const timeError = validateTimePreference(formData.timePreference);
    if (timeError) errors.timePreference = timeError;

    // If any validation errors, show them and stop
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      setIsSubmitting(false);
      return;
    }

    // Safeguard against adding the same employee twice. Email is read-only, so
    // surface any duplicate in the top banner rather than on a field.
    const duplicateError = await checkDuplicateEmail(
      formData.email,
      formData.userId,
    );
    if (duplicateError) {
      setError(duplicateError);
      setIsSubmitting(false);
      return;
    }

    try {
      await onSave(formData, formData.userId, isExistingRosterAgent);
      onClose();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "An unknown error occurred.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // ROBUST DEEP COMPARISON: Normalizes all fields to strings to prevent Type Mismatches
  // (e.g. 80 vs "80", null vs "")
  const checkHasChanges = () => {
    if (!initialData) return false;

    const normalizeForComparison = (data: EmployeeRosterPayload) => ({
      displayName: String(data.displayName || "").trim(),
      email: String(data.email || "").trim(),
      phoneNumber: String(data.phoneNumber || "").trim(),
      gender: String(data.gender || ""),
      efficiency: String(data.efficiency || ""),
      startTime: String(data.timePreference?.[0] ?? ""),
      endTime: String(data.timePreference?.[1] ?? ""),
      // Sort array so [Mon, Tue] and [Tue, Mon] are evaluated identically
      availability: [...(data.availability || [])].sort().join(","),
    });

    const currentNormalized = normalizeForComparison(formData);
    const baselineNormalized = normalizeForComparison(initialData);

    return JSON.stringify(currentNormalized) !== JSON.stringify(baselineNormalized);
  };

  const hasChanges = checkHasChanges();

  const daysOfWeek: DayOfWeek[] = [
    "Mon",
    "Tue",
    "Wed",
    "Thu",
    "Fri",
    "Sat",
    "Sun",
  ];
  // Read-only value box for the employee's identity fields (theme-aware).
  const readonlyBox =
    "mt-1 block w-full rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-100 dark:bg-gray-700 py-2.5 px-3 text-sm text-gray-900 dark:text-white break-words";
  const readonlyLabel = "block text-sm font-medium text-gray-900 dark:text-white";

  // Generate time options from 0 to 24 with half-hour increments
  const timeOptions: number[] = [];
  for (let i = 0; i <= 24; i += 0.5) {
    timeOptions.push(i);
  }

  // Format time for display (e.g., 9 -> "09:00", 9.5 -> "09:30")
  const formatTimeForDisplay = (hour: number): string => {
    const h = Math.floor(hour);
    const m = (hour % 1) * 60;
    return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`;
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      {...(!isCheckingExistence && {
        header: isExistingRosterAgent ? "Edit Agent" : "Add Agent to Roster",
      })}
    >
      {isCheckingExistence ? (
        <Loader />
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="text-red-600 dark:text-red-300 bg-red-100 dark:bg-red-900/30 border border-red-200 dark:border-red-800 p-3 rounded-md text-sm">
              {error}
            </div>
          )}
          {/* Employee identity — read-only, pulled from the employee record.
              These are display-only (no inputs, no validation). */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={readonlyLabel}>Name</label>
              <p className={readonlyBox}>{formData.displayName || "—"}</p>
            </div>
            <div>
              <label className={readonlyLabel}>Email</label>
              <p className={readonlyBox}>{formData.email || "—"}</p>
            </div>
            <div>
              <label className={readonlyLabel}>Phone Number</label>
              <p className={readonlyBox}>{formData.phoneNumber || "—"}</p>
            </div>
            <div>
              <label className={readonlyLabel}>Gender</label>
              <p className={readonlyBox}>{formData.gender || "—"}</p>
            </div>
          </div>
          <div>
            <label
              htmlFor="efficiency"
              className="block text-sm font-medium text-gray-900 dark:text-white"
            >
              Efficiency ({Math.round(formData.efficiency)}%)
            </label>
            <input
              type="range"
              name="efficiency"
              id="efficiency"
              min="0"
              max="100"
              step="1"
              value={formData.efficiency}
              onChange={handleChange}
              className="mt-1 block w-full"
            />
          </div>

          {/* Time Preference Section */}
          <div>
            <label className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
              Time Preference <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label
                  htmlFor="startTime"
                  className="block text-sm text-gray-600 dark:text-gray-300 mb-1"
                >
                  Start Time
                </label>
                <select
                  id="startTime"
                  value={formData.timePreference[0]}
                  onChange={(e) => handleTimePreferenceChange(0, e.target.value)}
                  className={`block w-full border rounded-lg bg-gray-50 dark:bg-gray-700 py-2.5 px-3 focus:outline-none focus:ring-primary max-h-48 overflow-y-auto text-sm text-gray-900 dark:text-white ${validationErrors.timePreference
                      ? "border-red-500 focus:border-red-500"
                      : "border-gray-300 dark:border-gray-600 focus:border-primary"
                    }`}
                >
                  {timeOptions.slice(0, -1).map((time) => (
                    <option key={time} value={time}>
                      {formatTimeForDisplay(time)}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label
                  htmlFor="endTime"
                  className="block text-sm text-gray-600 dark:text-gray-300 mb-1"
                >
                  End Time
                </label>
                <select
                  id="endTime"
                  value={formData.timePreference[1]}
                  onChange={(e) => handleTimePreferenceChange(1, e.target.value)}
                  className={`block w-full border rounded-lg bg-gray-50 dark:bg-gray-700 py-2.5 px-3 focus:outline-none focus:ring-primary max-h-48 overflow-y-auto text-sm text-gray-900 dark:text-white ${validationErrors.timePreference
                      ? "border-red-500 focus:border-red-500"
                      : "border-gray-300 dark:border-gray-600 focus:border-primary"
                    }`}
                >
                  {timeOptions.slice(1).map((time) => (
                    <option key={time} value={time}>
                      {formatTimeForDisplay(time)}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            {validationErrors.timePreference && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                {validationErrors.timePreference}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-900 dark:text-white">
              Availability <span className="text-red-500">*</span>
            </label>
            <div
              className={`mt-2 grid grid-cols-4 gap-2 p-3 rounded-md border ${validationErrors.availability
                  ? "border-2 border-red-500 bg-red-50 dark:bg-red-900/20"
                  : "border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/40"
                }`}
            >
              {daysOfWeek.map((day) => (
                <label
                  key={day}
                  className="flex items-center gap-2 text-sm text-gray-900 dark:text-gray-100 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={formData.availability.includes(day)}
                    onChange={() => handleAvailabilityChange(day)}
                    className="h-4 w-4 rounded border-gray-300 dark:border-gray-500 bg-white dark:bg-gray-700 text-primary focus:ring-primary focus:ring-offset-0"
                  />
                  <span>{day}</span>
                </label>
              ))}
            </div>
            {validationErrors.availability && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                {validationErrors.availability}
              </p>
            )}
          </div>
          <div className="pt-4 flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="bg-gray-200 text-gray-700 font-semibold px-4 py-2 rounded-lg hover:bg-gray-300 transition-colors duration-200 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !hasChanges}
              style={{ backgroundColor: colors.primary }}
              className="text-white font-semibold px-4 py-2 rounded-lg shadow-md hover:opacity-90 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isExistingRosterAgent
                ? isSubmitting
                  ? "Saving..."
                  : "Update Agent"
                : isSubmitting
                  ? "Saving..."
                  : "Save Agent"}
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
};

export default AgentFormModal;